// code-guardian-ignore-duplicates: deliberately-parallel report page — shares the
// filter/table/CSV scaffolding of QuizPerformanceReportPage by design so the two admin
// reports behave identically; the columns and status vocabulary are SCORM-specific.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Search, Filter, GraduationCap, ChevronDown, X, Eye } from 'lucide-react';
import { lmsAPI } from '../services/api';
import { downloadCsvLegacy } from '../utils/download';
import { useAuthStore } from '../stores/authStore';
import ScormLearnerDetailDrawer from '../components/ScormLearnerDetailDrawer';
import { BlockLoader } from '../components/loading';
import { useTableSort, SortTh } from '../components/dashboard/useTableSort';

// SCORM's unified vocabulary (see unifyStatus in lms-scorm.controller.ts), which is
// wider than the native quiz statuses — 'browsed' and 'not_attempted' have no quiz analogue.
type ScormStatus = 'not_attempted' | 'in_progress' | 'browsed' | 'completed' | 'passed' | 'failed';

interface ScormRow {
  employee_code: string;
  employee_name: string;
  module_id: string;
  module_title: string;
  pass_score: number;
  lesson_status: ScormStatus;
  score_raw: number | null;
  score_scaled: number | null;
  first_score: number | null;
  passed_on_attempt: number | null;
  total_time_seconds: number;
  attempts: number;
  started_at: string | null;
  completed_at: string | null;
  last_activity_at: string | null;

  // CMI-derived. Denormalized onto lms_scorm_runtime when the course commits, and
  // backfilled from cmi_json for rows written before that, so historical learners are
  // populated too. Anything the package never reported stays null.
  completion_status: string | null;
  success_status: string | null;
  lesson_location: string | null;
  entry: string | null;
  exit: string | null;
  points_awarded: number;
  scorm_version: string | null;
  mastery_score: number | null;
  progress_measure: number | null;
  interactions_total: number;
  interactions_correct: number;
  objectives_total: number;
  objectives_completed: number;
  suspend_data_bytes: number;
  has_cmi: boolean;
}

interface ModuleOption {
  id: string;
  title: string;
  status: string;
}

const statusConfig: Record<ScormStatus, { label: string; bg: string; fg: string }> = {
  not_attempted: { label: 'Not Started', bg: 'bg-gray-100', fg: 'text-gray-700' },
  in_progress: { label: 'In Progress', bg: 'bg-yellow-100', fg: 'text-yellow-700' },
  browsed: { label: 'Browsed', bg: 'bg-blue-100', fg: 'text-blue-700' },
  completed: { label: 'Completed', bg: 'bg-green-100', fg: 'text-green-700' },
  passed: { label: 'Passed', bg: 'bg-green-100', fg: 'text-green-700' },
  failed: { label: 'Failed', bg: 'bg-red-100', fg: 'text-red-700' },
};

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// cmi total_time arrives as accumulated seconds across sessions.
const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return '<1m';
};

// Epoch ms, so date columns sort chronologically (null → sinks to the bottom).
const toMs = (iso: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

interface ScormProgressReportPageProps {
  /** Rendered inside another report (e.g. the Quiz Performance tabs) — suppresses
   *  this page's own title block so there is only one heading on screen. */
  embedded?: boolean;
}

export default function ScormProgressReportPage({ embedded = false }: ScormProgressReportPageProps = {}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const projectId = selectedProjectId ?? undefined;

  const [rows, setRows] = useState<ScormRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [statusFilter, setStatusFilter] = useState<ScormStatus | 'all'>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);
  const [showModulePicker, setShowModulePicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);

  const [modules, setModules] = useState<ModuleOption[]>([]);
  // The row whose full CMI record is open in the drawer.
  const [detailRow, setDetailRow] = useState<ScormRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Only SCORM modules can appear in this report, so filter the picker to them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await lmsAPI.getContentModules({ status: 'published', projectId });
        if (cancelled) return;
        const data = resp.data?.data || [];
        setModules(
          data
            .filter((m: any) => m.module_type === 'scorm')
            .map((m: any) => ({ id: String(m.id), title: m.title, status: m.status })),
        );
      } catch (err) {
        console.warn('Could not load module list:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const fetchReport = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const resp = await lmsAPI.getScormProgressReport({
        fromDate: startDate || undefined,
        toDate: endDate || undefined,
        moduleIds: selectedModuleIds.length > 0 ? selectedModuleIds : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        q: searchQuery || undefined,
        projectId,
      });
      const data = resp.data?.data || [];
      const num = (v: any) => (v === null || v === undefined || v === '' ? null : Number(v));
      setRows(data.map((r: any) => ({
        employee_code: r.employee_code,
        employee_name: r.employee_name || r.employee_code,
        module_id: r.module_id,
        module_title: r.module_title,
        pass_score: parseInt(r.pass_score) || 0,
        lesson_status: (r.lesson_status || 'not_attempted') as ScormStatus,
        score_raw: num(r.score_raw),
        score_scaled: num(r.score_scaled),
        first_score: num(r.first_score),
        passed_on_attempt: num(r.passed_on_attempt),
        total_time_seconds: parseInt(r.total_time_seconds) || 0,
        attempts: parseInt(r.attempts) || 0,
        started_at: r.started_at,
        completed_at: r.completed_at,
        last_activity_at: r.last_activity_at,

        completion_status: r.completion_status ?? null,
        success_status: r.success_status ?? null,
        lesson_location: r.lesson_location ?? null,
        entry: r.entry ?? null,
        exit: r.exit ?? null,
        points_awarded: parseInt(r.points_awarded) || 0,
        scorm_version: r.scorm_version ?? null,
        mastery_score: num(r.mastery_score),
        progress_measure: num(r.progress_measure),
        interactions_total: parseInt(r.interactions_total) || 0,
        interactions_correct: parseInt(r.interactions_correct) || 0,
        objectives_total: parseInt(r.objectives_total) || 0,
        objectives_completed: parseInt(r.objectives_completed) || 0,
        suspend_data_bytes: parseInt(r.suspend_data_bytes) || 0,
        has_cmi: Boolean(r.has_cmi),
      })));
    } catch (err) {
      console.error('Error loading SCORM progress report:', err);
      setError('Failed to load course progress report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, startDate, endDate, selectedModuleIds, statusFilter, searchQuery]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Mirrors STATUSES_WITHOUT_COMPLETION in lms-scorm.controller.ts.
  const dateRangeIgnored =
    statusFilter === 'not_attempted' || statusFilter === 'in_progress' || statusFilter === 'browsed';

  const moduleLabel = useMemo(() => {
    if (selectedModuleIds.length === 0) return 'All Courses';
    if (selectedModuleIds.length === 1) {
      const m = modules.find((mm) => mm.id === selectedModuleIds[0]);
      return m?.title ?? '1 course';
    }
    return `${selectedModuleIds.length} courses`;
  }, [selectedModuleIds, modules]);

  // Keyed by the header labels the table maps over; each value is what its cell shows
  // ('—' cells return null so they sink).
  const sort = useTableSort<ScormRow>(rows, {
    // An unresolved name falls back to the code and renders '—' — treat it as missing.
    'Employee Name': (r) => (r.employee_name === r.employee_code ? null : r.employee_name),
    'Employee Code': (r) => r.employee_code,
    Course: (r) => r.module_title,
    Status: (r) => statusConfig[r.lesson_status].label,
    Progress: (r) => r.progress_measure,
    Stage: (r) => r.lesson_location,
    Score: (r) => r.score_raw,
    Attempts: (r) => r.attempts || null,
    // "correct/total" and "completed/total" sort on the count shown first.
    Questions: (r) => (r.interactions_total > 0 ? r.interactions_correct : null),
    Objectives: (r) => (r.objectives_total > 0 ? r.objectives_completed : null),
    'Time Spent': (r) => (r.total_time_seconds > 0 ? r.total_time_seconds : null),
    Points: (r) => r.points_awarded || null,
    Started: (r) => toMs(r.started_at),
    Completed: (r) => toMs(r.completed_at),
    'Last Activity': (r) => toMs(r.last_activity_at),
  });

  const handleExportCSV = () => {
    if (rows.length === 0) return;
    // Every scalar the report holds. The two blob fields (suspend_data, the raw CMI
    // tree) are deliberately not columns — they are per-learner JSON of unbounded size
    // and would wreck the sheet; export them one learner at a time from the detail drawer.
    const header = [
      'Employee Code',
      'Employee Name',
      'Course',
      'SCORM Version',
      'Status',
      'Completion Status',
      'Success Status',
      'Progress (%)',
      'Lesson Location',
      'Score (%)',
      'Score (scaled)',
      'First Score (%)',
      'Attempts',
      'Passed On Attempt',
      'Interactions Correct',
      'Interactions Total',
      'Objectives Completed',
      'Objectives Total',
      'Time Spent (min)',
      'Points Awarded',
      'Pass Score (%)',
      'Mastery Score',
      'Entry',
      'Exit',
      'Suspend Data (bytes)',
      'Started At',
      'Completed At',
      'Last Activity',
    ];
    const lines = rows.map((r) => [
      r.employee_code,
      r.employee_name,
      r.module_title,
      r.scorm_version ?? '',
      statusConfig[r.lesson_status].label,
      r.completion_status ?? '',
      r.success_status ?? '',
      r.progress_measure !== null ? String(Math.round(r.progress_measure * 100)) : '',
      r.lesson_location ?? '',
      r.score_raw !== null ? String(r.score_raw) : '',
      r.score_scaled !== null ? String(r.score_scaled) : '',
      r.first_score !== null ? String(r.first_score) : '',
      String(r.attempts),
      r.passed_on_attempt !== null ? String(r.passed_on_attempt) : '',
      r.interactions_total > 0 ? String(r.interactions_correct) : '',
      r.interactions_total > 0 ? String(r.interactions_total) : '',
      r.objectives_total > 0 ? String(r.objectives_completed) : '',
      r.objectives_total > 0 ? String(r.objectives_total) : '',
      String(Math.round(r.total_time_seconds / 60)),
      String(r.points_awarded),
      String(r.pass_score),
      r.mastery_score !== null ? String(r.mastery_score) : '',
      r.entry ?? '',
      r.exit ?? '',
      r.suspend_data_bytes ? String(r.suspend_data_bytes) : '',
      r.started_at ? new Date(r.started_at).toISOString() : '',
      r.completed_at ? new Date(r.completed_at).toISOString() : '',
      r.last_activity_at ? new Date(r.last_activity_at).toISOString() : '',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadCsvLegacy(`course-progress-${startDate}-to-${endDate}.csv`, csv);
  };

  const toggleModule = (id: string) => {
    setSelectedModuleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className={embedded ? '' : 'space-y-6'}>
      <div className="flex items-center justify-between gap-4">
        {embedded ? (
          <p className="text-sm text-gray-500">
            SCORM course outcomes for assigned employees — who they are, whether they completed,
            time spent and how many attempts it took.
          </p>
        ) : (
          <div className="flex items-center gap-3">
            <GraduationCap className="h-7 w-7 text-primary-600" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Course Progress</h1>
              <p className="text-sm text-gray-500 mt-1">
                SCORM course outcomes for assigned employees — time spent and how many attempts it took.
              </p>
            </div>
          </div>
        )}
        <button
          onClick={handleExportCSV}
          disabled={rows.length === 0 || loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 shrink-0"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <section className={embedded ? "pt-4" : "rounded-lg border border-gray-100 bg-white p-6"}>
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <div className="grid grid-cols-2 gap-2">
            {dateRangeIgnored && (
              <p className="col-span-2 -mb-1 text-xs text-amber-600">
                Date range doesn't apply to {statusConfig[statusFilter as ScormStatus].label} — those courses have no completion date.
              </p>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Course multi-select */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Course</label>
            <button
              type="button"
              onClick={() => {
                setShowModulePicker((v) => !v);
                setShowStatusPicker(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              <span className="truncate">{moduleLabel}</span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {showModulePicker && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                <div className="flex items-center justify-between px-2 py-1">
                  <button onClick={() => setSelectedModuleIds([])} className="text-xs text-primary-600 hover:underline" type="button">
                    Clear
                  </button>
                  <button onClick={() => setShowModulePicker(false)} className="text-xs text-gray-500 hover:text-gray-700" type="button">
                    Close
                  </button>
                </div>
                {modules.length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-500">No published SCORM courses</p>
                )}
                {modules.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedModuleIds.includes(m.id)}
                      onChange={() => toggleModule(m.id)}
                      className="w-4 h-4"
                    />
                    <span className="truncate">{m.title}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <button
              type="button"
              onClick={() => {
                setShowStatusPicker((v) => !v);
                setShowModulePicker(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                {statusFilter === 'all' ? 'All Status' : statusConfig[statusFilter].label}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
            {showStatusPicker && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {(['all', 'passed', 'completed', 'failed', 'in_progress', 'browsed', 'not_attempted'] as const).map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => {
                      setStatusFilter(s);
                      setShowStatusPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    {s === 'all' ? 'All Status' : statusConfig[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Employee search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or code…"
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <BlockLoader label="Loading…" className="py-16 text-center" />
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">No course progress data for this filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-100">
                  {[
                    'Employee Name', 'Employee Code', 'Course', 'Status', 'Progress', 'Stage',
                    'Score', 'Attempts', 'Questions', 'Objectives', 'Time Spent', 'Points',
                    'Started', 'Completed', 'Last Activity',
                  ].map((h) => (
                    <SortTh key={h} label={h} sortKey={h} sort={sort} className="px-3 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap" />
                  ))}
                  <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">
                    <span className="sr-only">Details</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sort.sorted.map((r, i) => {
                  const sc = statusConfig[r.lesson_status];
                  // More than one scored submission means retakes — surface the
                  // starting score so a struggle is visible at a glance.
                  const struggled = r.attempts > 1 && r.first_score !== null;
                  return (
                    <tr key={`${r.employee_code}|${r.module_id}|${i}`} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {r.employee_name === r.employee_code
                          ? <span className="text-gray-400">—</span>
                          : r.employee_name}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{r.employee_code}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">
                        {r.module_title}
                        {r.scorm_version && (
                          <span className="ml-1.5 text-[11px] text-gray-400">SCORM {r.scorm_version}</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${sc.bg} ${sc.fg}`}>
                          {sc.label}
                        </span>
                      </td>
                      {/* progress_measure is SCORM 2004 only — 1.2 has no percent-complete
                          element at all, so an em dash here means "the standard doesn't
                          carry it", not "the learner did nothing". */}
                      <td className="px-3 py-3 text-sm text-gray-700">
                        {r.progress_measure !== null ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-block w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <span
                                className="block h-full bg-primary-500"
                                style={{ width: `${Math.min(100, Math.max(0, r.progress_measure * 100))}%` }}
                              />
                            </span>
                            <span className="text-xs">{Math.round(r.progress_measure * 100)}%</span>
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 max-w-[10rem]">
                        {r.lesson_location ? (
                          <span className="block truncate" title={r.lesson_location}>
                            {r.lesson_location}
                          </span>
                        ) : (
                          <span
                            className="text-gray-300"
                            title={r.suspend_data_bytes
                              ? 'No bookmark reported — this package stores its position inside suspend_data'
                              : 'No bookmark reported'}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {r.score_raw !== null ? `${r.score_raw}%` : '—'}
                        {struggled && (
                          <span className="ml-1 text-xs text-gray-400">(from {r.first_score}%)</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm whitespace-nowrap">
                        <span className={r.attempts > 1 ? 'text-amber-600 font-medium' : 'text-gray-700'}>
                          {r.attempts || '—'}
                        </span>
                        {r.passed_on_attempt !== null && r.passed_on_attempt > 1 && (
                          <span className="ml-1 text-xs text-gray-400">passed on #{r.passed_on_attempt}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {r.interactions_total > 0
                          ? `${r.interactions_correct}/${r.interactions_total}`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {r.objectives_total > 0
                          ? `${r.objectives_completed}/${r.objectives_total}`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDuration(r.total_time_seconds)}</td>
                      <td className="px-3 py-3 text-sm text-gray-700">{r.points_awarded || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(r.started_at)}</td>
                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(r.completed_at)}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(r.last_activity_at)}</td>
                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDetailRow(r)}
                          disabled={!r.has_cmi}
                          title={r.has_cmi
                            ? 'Full CMI record: interactions, objectives, bookmark, raw tree'
                            : 'Nothing recorded yet — this learner has not opened the course'}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-3">
              {rows.length} row{rows.length === 1 ? '' : 's'}{rows.length >= 5000 ? ' (capped at 5000 — narrow your filters)' : ''}
            </p>
          </div>
        )}
      </section>

      {(showModulePicker || showStatusPicker) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowModulePicker(false);
            setShowStatusPicker(false);
          }}
        />
      )}

      {detailRow && (
        <ScormLearnerDetailDrawer
          moduleId={detailRow.module_id}
          employeeCode={detailRow.employee_code}
          projectId={projectId}
          fallbackName={detailRow.employee_name}
          fallbackCourse={detailRow.module_title}
          onClose={() => setDetailRow(null)}
        />
      )}
    </div>
  );
}
